import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { PendingRefusal } from '../lib/agent-model-save';
import type { StaleWinner } from '../lib/route-refusal';
import { Button } from './ui/Button';

// A refused model save, inline on its card (#5210, epic #5200) — the refusal
// `lib/route-refusal.ts` classified, and the one cure each kind admits:
//   - stale (409)      -> who saved first, and "Apply mine anyway";
//   - reasoning (422)  -> the API's own detail, and "Apply and reset thinking depth";
//   - any other 422    -> the API's own detail, NO cure (a reset cannot fix it);
//   - locked (403)     -> "Ask an admin";
//   - account_changed  -> the org switched under it; refused unsent, NO cure;
//   - failed           -> the detail, or the generic copy.
// Presenter: the card's save controller owns the refusal and the retries.

/** Who won a stale race, as the notice names them — shared with any other
 * `RouteRefusal` reader (`AgentModeSwitch`, `AgentRepoOverridesSection`,
 * #5211): one mapping from `StaleWinner` to a sentence, never a second copy. */
export const who = (w: StaleWinner): string => {
  switch (w.kind) {
    case 'member':
      return w.login === null ? t('agents.save.who.member') : `@${w.login}`;
    case 'token':
      return t('agents.save.who.token');
    case 'unknown':
      return t('agents.save.who.unknown');
  }
};

/** The sentence a refusal reads — the API's own `detail` wherever it sent one. */
function noticeText({ refusal, intent }: PendingRefusal): string {
  switch (refusal.kind) {
    case 'account_changed':
      return intent.kind === 'restore'
        ? t('agents.save.account_changed_undo')
        : t('agents.save.account_changed');
    case 'stale':
      return refusal.cleared
        ? t('agents.save.stale_cleared', { who: who(refusal.who) })
        : t('agents.save.stale', { who: who(refusal.who) });
    case 'locked':
      return t('agents.save.locked');
    case 'reasoning':
      return refusal.detail;
    case 'refused':
    case 'failed':
      return refusal.detail ?? t('agents.save.failed');
  }
}

export type ModelSaveNoticeProps = {
  readonly pending: PendingRefusal | null;
  readonly busy: boolean;
  readonly onApplyAnyway: () => void;
  readonly onApplyReset: () => void;
  readonly onDismiss: () => void;
};

export function ModelSaveNotice(props: ModelSaveNoticeProps): JSX.Element {
  return (
    <Show when={props.pending}>
      {(pending) => (
        <div class="model-save-notice" role="alert" data-refusal={pending().refusal.kind}>
          <p class="model-save-notice__text">{noticeText(pending())}</p>
          <div class="model-save-notice__actions">
            <Show when={pending().refusal.kind === 'stale'}>
              <Button
                size="sm"
                variant="outline"
                disabled={props.busy}
                onClick={props.onApplyAnyway}
              >
                {t('agents.save.apply_anyway')}
              </Button>
            </Show>
            <Show when={pending().refusal.kind === 'reasoning'}>
              <Button
                size="sm"
                variant="outline"
                disabled={props.busy}
                onClick={props.onApplyReset}
              >
                {t('agents.save.apply_reset')}
              </Button>
            </Show>
            <Button size="sm" variant="ghost" onClick={props.onDismiss}>
              {t('agents.save.dismiss')}
            </Button>
          </div>
        </div>
      )}
    </Show>
  );
}
